import React, {
  Suspense,
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Portal, Box, useDisclosure, Flex, Icon } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { MdAnalytics, MdGroups, MdWorkspaces } from "react-icons/md";

// Layout components
import Footer from "common/components/footer/FooterAdmin.js";
import Navbar from "common/components/navbar/NavbarAdmin.js";
import Sidebar from "common/components/sidebar/Sidebar.js";
import { SidebarContext } from "common/contexts/SidebarContext";
import FilterSidebar from "common/components/filterSidebar/FilterSidebar";

// Routes
import { mainRoutes } from "common/routes.js";

// Utils
import { ROLE_PATH } from "common/roles";
import { fetchRouteData } from "common/redux/reducers/routeSlice";
import { fetchImage } from "common/redux/reducers/imageSlice";
import { ROLE } from "common/roles";
import { accessModuleTitles } from "common/constant";
import { getApi } from "common/services/api";
import { API_PATHS } from "common/services/apiPaths";
import { getActiveRole, getEffectiveRoleName } from "common/utils/roleUtils";

export default function UnifiedDashboard(props) {
  const { ...rest } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const { onOpen } = useDisclosure();

  // Use ref to prevent re-initialization
  const isInitializedRef = useRef(false);

  // States
  const [fixed] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [openSidebar, setOpenSidebar] = useState(true);
  const [openSubBar, setOpenSubBar] = useState(true);
  const [subRoute, setSubRoute] = useState([]);
  const [isReportingManager, setIsReportingManager] = useState(false);

  // Get user from Redux to ensure reactivity when role changes
  const { user: reduxUser } = useSelector((state) => state.user);

  const user = useMemo(() => {
    try {
      return reduxUser?.user || reduxUser || JSON.parse(localStorage.getItem("user") || "{}");
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  }, [reduxUser]);

  const userRole = useMemo(() => {
    if (!user) return null;

    const role = getEffectiveRoleName(user);

    const raw = String(role || "").trim();
    const lower = raw.toLowerCase();
    // Normalize role names coming from DB (often Title Case) to app constants.
    if (lower === "employee") return ROLE.employee;
    if (lower === "reviewer") return ROLE.reviewer;
    if (lower === "recruiter") return ROLE.recruiter;
    if (lower === "user") return ROLE.user;
    if (lower === "admin") return ROLE.admin;
    if (lower === "super admin" || lower === "superadmin" || lower === "super_admin") return ROLE.superAdmin;
    if (lower === "hiring manager" || lower === "hiringmanager") return ROLE.hiringManager;
    return raw;
  }, [user]);

  const isAdminUser = useMemo(() => {
    if (!userRole) return false;
    return ['admin', 'superadmin', 'super admin'].includes(userRole.toLowerCase());
  }, [userRole]);

  useEffect(() => {
    let mounted = true;

    const checkReportingManager = async () => {
      if (!user?._id) {
        if (mounted) setIsReportingManager(false);
        return;
      }

      try {
        const response = await getApi(API_PATHS.RECRUITMENT_DASHBOARD);
        const performance = response?.data?.performance || {};
        const isManagerOfAnyUser = Boolean(
          performance.isReportingManager ||
          (performance.directReportCount ?? 0) > 0
        );

        console.log("[Performance][TeamReviews] reporting-manager-check", {
          currentUserId: String(user._id || ''),
          activeRole: userRole,
          isManagerOfAnyUser,
          directReportCount: performance.directReportCount ?? 0,
          source: "dashboard-metrics",
        });

        if (mounted) setIsReportingManager(isManagerOfAnyUser);
      } catch (error) {
        console.error("Error checking reporting manager status:", error);
        if (mounted) setIsReportingManager(false);
      }
    };

    checkReportingManager();

    return () => {
      mounted = false;
    };
  }, [user?._id, userRole]);

  // Simple permission check function - uses activeRoleId if available
  const hasViewAccess = useCallback((user, moduleName) => {
    if (!user) return true;

    // If no module specified, allow access (no permission required)
    if (!moduleName || moduleName === null) return true;

    if (
      moduleName === "Team Reviews" &&
      userRole === ROLE.employee &&
      isReportingManager
    ) {
      return true;
    }

    if (user.role === "superAdmin") {
      return true;
    }

    // Robust Admin Check
    const activeRoleName = getEffectiveRoleName(user);

    const isAdmin =
      (activeRoleName && ['admin', 'super admin', 'superadmin'].includes(activeRoleName.toLowerCase())) ||
      (user.role && ['admin', 'super admin', 'superadmin'].includes(user.role.toLowerCase())) ||
      (user?.roles && Array.isArray(user.roles) && user.roles.some(r => {
        const rName = (r.roleName || r.role || '').toLowerCase();
        return ['admin', 'super admin', 'superadmin'].includes(rName);
      }));

    if (isAdmin) {
      return true;
    }

    if (!user.roles || !Array.isArray(user.roles)) {
      return true;
    }

    try {
      // Use activeRoleId if available, otherwise check all roles
      const activeRole = getActiveRole(user);
      const rolesToCheck = activeRole ? [activeRole] : user.roles;

      // Check if module exists in any role's access list
      for (const role of rolesToCheck) {
        const accessItems = Array.isArray(role.access)
          ? role.access
          : [role.access];
        for (const item of accessItems) {
          if (item && item.title === moduleName) {
            return item.view === true;
          }
        }
      }

      // For Performance modules specifically, deny if not in access list
      const performanceModules = [
        "My Reviews", "Reviews About Me", "Team Reviews",
        "Review Templates", "Review Cycles", "Assignments", "Approvals", "Performance Analytics"
      ];

      if (performanceModules.includes(moduleName)) {
        return false; // Strict permission check for performance modules
      }

      // For other modules, allow by default (backward compatibility)
      return true;
    } catch (error) {
      console.error("Error checking permissions:", error);
      return true;
    }
  }, [isReportingManager, userRole]);

  // Redux selectors - Fix the memoization issue
  const largeLogo = useSelector(
    (state) => {
      const images = state?.images?.image;
      if (!images || !Array.isArray(images)) return [];
      return images.filter((item) => item.isActive === true);
    },
    (left, right) => JSON.stringify(left) === JSON.stringify(right)
  );

  // Filter routes for SIDEBAR ONLY (excludes hidden routes)
  const filteredSidebarRoutes = useMemo(() => {
    if (!mainRoutes || !Array.isArray(mainRoutes)) {
      return [];
    }

    // Check if user's ACTIVE role is employee (not just if they have employee in roles array)
    const isEmployee = userRole === ROLE.employee;

    // Check if user's ACTIVE role is reviewer
    const isReviewer = userRole === ROLE.reviewer || userRole === "reviewer";

    try {
      // For REVIEWERS: show Dashboard, Todo, Performance (Peer Reviews), Document Verification and Onboarding Checklist
      if (isReviewer) {
        const filtered = mainRoutes.reduce((acc, route) => {
          if (route.hidden) return acc;

          // Show Dashboard and Todo
          if (route.name === "Dashboard" || route.name === "Todo") {
            acc.push(route);
            return acc;
          }

          // Show Performance category with filtered subroutes
          if (route.name === "Performance" && route.subRoutes && Array.isArray(route.subRoutes)) {
            const filteredPerformanceSubRoutes = route.subRoutes.filter((subRoute) => {
              if (subRoute.hidden) return false;
              const hasLayout =
                !subRoute.layout ||
                (Array.isArray(subRoute.layout) && subRoute.layout.includes(ROLE_PATH.reviewer));
              if (!hasLayout) return false;
              return subRoute.module ? hasViewAccess(user, subRoute.module) : true;
            });

            if (filteredPerformanceSubRoutes.length > 0) {
              acc.push({
                ...route,
                subRoutes: filteredPerformanceSubRoutes
              });
            }
            return acc;
          }

          // For Recruitment category, only show Document Verification and Onboarding Checklist
          if (route.subRoutes && Array.isArray(route.subRoutes)) {
            const filteredSubRoutes = route.subRoutes.filter((subRoute) => {
              return subRoute.module === "Document Verification" || subRoute.module === "Onboarding Checklist";
            });

            if (filteredSubRoutes.length > 0) {
              acc.push({
                ...route,
                subRoutes: filteredSubRoutes
              });
            }
            return acc;
          }

          // For top-level routes, only show Document Verification and Onboarding Checklist
          if (route.module === "Document Verification" || route.module === "Onboarding Checklist") {
            acc.push(route);
          }

          return acc;
        }, []);

        return filtered || [];
      }

      // For EMPLOYEES: hide Recruitment category and promote specific sub-routes to root level
      if (isEmployee) {
        const dashboardRoute = mainRoutes.find((r) => r.name === "Dashboard");
        const todoRoute = mainRoutes.find((r) => r.name === "Todo");
        const performanceRoute = mainRoutes.find((r) => r.name === "Performance" && r.isCategory);
        const recruitmentRoute = mainRoutes.find((r) => r.name === accessModuleTitles.Recruitment && r.isCategory);
        const resourcesRoute = mainRoutes.find((r) => r.name === "Resources" && r.isCategory);

        const getRecruitSubRoute = (name) =>
          recruitmentRoute?.subRoutes?.find(
            (sr) => sr.name === name || (name === accessModuleTitles.Feedback && sr.name === accessModuleTitles.Feedbacks)
          );

        const cloneSubRoute = (route, renamedLabel) => {
          if (!route) return null;
          // Ensure sub-routes are always leaf items in the sidebar (never expandable categories).
          const { subRoutes, isCategory, ...rest } = route;
          return {
            ...rest,
            name: renamedLabel || route.name,
            isCategory: false,
          };
        };

        // Team Reviews only shows for employees who are reporting managers of any user.

        const performanceSubRoutes = [];
        if (performanceRoute?.subRoutes?.length) {
          const myReviews = performanceRoute.subRoutes.find((sr) => sr.name === "Self Reviews");
          // Use path-based matching to avoid accidental swaps when names change or array order differs.
          const teamReviewsRoute = performanceRoute.subRoutes.find(
            (sr) => sr.path === "/performance/team-reviews" || sr.name === "Team Reviews"
          );
          const peerReviewsRoute = performanceRoute.subRoutes.find(
            (sr) => sr.path === "/performance/assigned-reviews" || sr.name === "Peer Reviews"
          );

          if (myReviews?.module && hasViewAccess(user, myReviews.module)) {
            performanceSubRoutes.push(cloneSubRoute(myReviews, "Self Reviews"));
          }
          // Peer Reviews should be visible as its own item for employees only.
          if (peerReviewsRoute && hasViewAccess(user, peerReviewsRoute.module)) {
            performanceSubRoutes.push(cloneSubRoute(peerReviewsRoute, "Peer Reviews"));
          }
          // Allow module-less routes (module: null) which are intentionally not gated by RBAC.
          // Team Reviews should only show for employees who are reporting managers of at least one user.
          if (isReportingManager && teamReviewsRoute && hasViewAccess(user, teamReviewsRoute.module)) {
            performanceSubRoutes.push(cloneSubRoute(teamReviewsRoute, "Team Reviews"));
          }
        }

        const careerSubRoutes = [];
        const jobOpenings = getRecruitSubRoute(accessModuleTitles.JobOpenings);
        const applications = getRecruitSubRoute(accessModuleTitles.Applications);
        const interviews = getRecruitSubRoute(accessModuleTitles.Interviews);
        const feedbackRoute = getRecruitSubRoute(accessModuleTitles.Feedback);
        if (jobOpenings?.module && hasViewAccess(user, jobOpenings.module)) {
          careerSubRoutes.push(cloneSubRoute(jobOpenings, "Job Openings"));
        }
        if (applications?.module && hasViewAccess(user, applications.module)) {
          careerSubRoutes.push(cloneSubRoute(applications, "My Applications"));
        }
        if (interviews?.module && hasViewAccess(user, interviews.module)) {
          careerSubRoutes.push(cloneSubRoute(interviews, "My Interviews"));
        }
        if (feedbackRoute?.module && hasViewAccess(user, feedbackRoute.module)) {
          careerSubRoutes.push(cloneSubRoute(feedbackRoute, "My Feedbacks"));
        }

        const referralSubRoutes = [];
        const referrals = getRecruitSubRoute(accessModuleTitles.Referrals);
        if (referrals?.module && hasViewAccess(user, referrals.module)) {
          referralSubRoutes.push(cloneSubRoute(referrals, "My Referrals"));
        }

        const resourcesSubRoutes = [];
        if (resourcesRoute?.subRoutes?.length) {
          resourcesRoute.subRoutes.forEach((subRoute) => {
            if (subRoute.hidden) return;
            if (subRoute.hideForEmployee) return;
            if (subRoute.layout && Array.isArray(subRoute.layout)) {
              if (!subRoute.layout.includes(ROLE_PATH.user)) return;
            }
            if (subRoute.module && !hasViewAccess(user, subRoute.module)) return;
            resourcesSubRoutes.push(cloneSubRoute(subRoute));
          });
        }

        const employeeModules = [
          dashboardRoute ? { ...cloneSubRoute(dashboardRoute, "Dashboard"), order: 1 } : null,
          todoRoute ? { ...cloneSubRoute(todoRoute, "Todo"), order: 2 } : null,
          performanceSubRoutes.length > 0 ? {
            ...(performanceRoute || {}),
            name: "Performance",
            path: "/performance",
            isCategory: true,
            order: 3,
            subRoutes: performanceSubRoutes,
          } : null,
          careerSubRoutes.length > 0 ? {
            name: "Career",
            path: "/career",
            icon: recruitmentRoute?.icon,
            isCategory: true,
            order: 4,
            subRoutes: careerSubRoutes,
          } : null,
          referralSubRoutes.length > 0 ? {
            name: "Referrals",
            path: "/referrals",
            icon: referrals?.icon,
            isCategory: true,
            order: 5,
            subRoutes: referralSubRoutes,
          } : null,
          resourcesSubRoutes.length > 0 ? {
            ...(resourcesRoute || {}),
            name: "Resources",
            path: "/resources",
            icon: resourcesRoute?.icon,
            isCategory: true,
            order: 6,
            subRoutes: resourcesSubRoutes,
          } : null,
        ].filter(Boolean);

        return employeeModules.sort((a, b) => (a.order || 999) - (b.order || 999));
      }

      // For OTHER ROLES (superAdmin, recruiter, hiringManager, etc.)
      let filtered = mainRoutes
        .filter((route) => {
          if (route.hidden) return false;

          if (route.layout && Array.isArray(route.layout)) {
            let allowedRolePath = ROLE_PATH.user;
            if (userRole === "superAdmin") {
              allowedRolePath = ROLE_PATH.superAdmin;
            }

            const hasLayoutAccess = route.layout.includes(allowedRolePath);
            const isEmployeeBlocked = route.layout.includes(ROLE_PATH.employeeNotAllowed) && userRole === "employee";

            if (!hasLayoutAccess || isEmployeeBlocked)
              return false;
          }

          if (route.isCategory && route.subRoutes && Array.isArray(route.subRoutes)) {
            const hasAnySubrouteAccess = route.subRoutes.some((subRoute) => {
              if (subRoute.hidden) return false;
              if (subRoute.adminOnly) return isAdminUser;
              // Respect sub-route layout (e.g., employee-only pages should not appear for recruiter/hiringManager)
              if (subRoute.layout && Array.isArray(subRoute.layout)) {
                const allowedRolePath = userRole === "superAdmin" ? ROLE_PATH.superAdmin : ROLE_PATH.user;
                if (!subRoute.layout.includes(allowedRolePath)) return false;
              }
              return subRoute.module ? hasViewAccess(user, subRoute.module) : true;
            });
            return hasAnySubrouteAccess;
          }

          if (route.module && userRole !== "superAdmin") {
            return hasViewAccess(user, route.module);
          }

          return true;
        })
        .map((route) => {
          if (route.subRoutes && Array.isArray(route.subRoutes)) {
            return {
              ...route,
              subRoutes: route.subRoutes.filter((subRoute) => {
                if (subRoute.adminOnly && !isAdminUser) {
                  return false;
                }
                // Respect layout for non-employee roles so employee-only modules don't leak into other sidebars.
                if (subRoute.layout && Array.isArray(subRoute.layout)) {
                  const allowedRolePath = userRole === "superAdmin" ? ROLE_PATH.superAdmin : ROLE_PATH.user;
                  if (!subRoute.layout.includes(allowedRolePath)) {
                    return false;
                  }
                }
                if (subRoute.module && !isAdminUser) {
                  return hasViewAccess(user, subRoute.module);
                }
                return true;
              }),
            };
          }
          return route;
        }).filter((item) => {
          const hasValidSubRoutes = !item.subRoutes || (item.subRoutes && item.subRoutes.length > 0);
          const hasName = item.name && item.name.trim().length > 0;
          return hasValidSubRoutes && hasName;
        });

      const roleSpecificSidebarUsers = [
        ROLE.admin,
        ROLE.recruiter,
        ROLE.hiringManager,
      ];

      if (!roleSpecificSidebarUsers.includes(userRole)) {
        return filtered || [];
      }

      const cloneRoute = (route, overrides = {}) => {
        if (!route) return null;
        return {
          ...route,
          ...overrides,
          subRoutes: overrides.subRoutes ?? route.subRoutes,
        };
      };

      const findRoute = (name) => filtered.find((route) => route.name === name);
      const findSubRoute = (routeName, subRouteName) =>
        findRoute(routeName)?.subRoutes?.find((subRoute) => subRoute.name === subRouteName);

      const compactCategory = (name, path, icon, subRoutes = []) => {
        const visibleSubRoutes = subRoutes.filter(Boolean);
        if (!visibleSubRoutes.length) return null;
        return {
          name,
          path,
          icon,
          isCategory: true,
          subRoutes: visibleSubRoutes,
        };
      };

      const dashboardRoute = findRoute("Dashboard");
      const todoRoute = findRoute("Todo") || findSubRoute("Tools", "Todo");
      const recruitmentRoute = findRoute("Recruitment");
      const performanceRoute = findRoute("Performance");
      const organizationRoute = findRoute("Organization");
      const resourcesRoute = findRoute("Resources");
      const administrationRoute = findRoute("Administration");

      const reportsRoute = findSubRoute("Organization", "Reports");
      const scheduledReportsRoute = findSubRoute("Organization", "Scheduled Reports");

      const recruitmentSection = compactCategory(
        "Recruitment",
        "/recruitment",
        <Icon as={MdGroups} width="20px" height="20px" color="inherit" />,
        [
          compactCategory(
            "Jobs",
            "/recruitment/jobs",
            null,
            [cloneRoute(findSubRoute("Recruitment", "Job Openings"))]
          ),
          compactCategory("Pipeline", "/recruitment/pipeline", null, [
            cloneRoute(findSubRoute("Recruitment", "Candidates")),
            cloneRoute(findSubRoute("Recruitment", "Applications")),
            cloneRoute(findSubRoute("Recruitment", "Interviews")),
            cloneRoute(findSubRoute("Recruitment", "Feedbacks"), { name: "Feedback" }),
          ]),
          compactCategory("Offers & Closure", "/recruitment/offers-and-closure", null, [
            cloneRoute(findSubRoute("Recruitment", "Document Verification")),
            cloneRoute(findSubRoute("Recruitment", "LOI")),
            cloneRoute(findSubRoute("Recruitment", "Offers")),
          ]),
          compactCategory("Onboarding", "/recruitment/onboarding", null, [
            cloneRoute(findSubRoute("Recruitment", "Onboarding Checklist")),
            cloneRoute(findSubRoute("Recruitment", "BG Verification")),
          ]),
          compactCategory("Sourcing", "/recruitment/sourcing", null, [
            cloneRoute(findSubRoute("Recruitment", "Referrals")),
          ]),
        ]
      );

      const reportsSection = compactCategory(
        "Analytics",
        "/reports",
        <Icon as={MdAnalytics} width="20px" height="20px" color="inherit" />,
        [
          cloneRoute(reportsRoute),
          cloneRoute(scheduledReportsRoute),
        ]
      );

      const knowledgeBaseSection = compactCategory("Knowledge Base", "/knowledge-base", resourcesRoute?.icon, [
        compactCategory("Policies & Guides", "/knowledge-base/policies-and-guides", null, [
          cloneRoute(findSubRoute("Resources", "Company Policies")),
          cloneRoute(findSubRoute("Resources", "Recruitment Guide"), { name: "Hiring Playbook" }),
          cloneRoute(findSubRoute("Resources", "Benefits Guide")),
        ]),
        compactCategory("Calendar", "/knowledge-base/calendar", null, [
          cloneRoute(findSubRoute("Resources", "Holiday Calendar")),
        ]),
        compactCategory("Templates", "/knowledge-base/templates", null, [
          cloneRoute(findSubRoute("Tools", "Email Templates")),
          cloneRoute(findSubRoute("Tools", "Offer Templates")),
          cloneRoute(findSubRoute("Tools", "Checklist Templates")),
        ]),
      ]);

      const setupSection = compactCategory("Setup", "/setup", administrationRoute?.icon || organizationRoute?.icon, [
        compactCategory("Users & Access", "/setup/users-and-access", null, [
          cloneRoute(findSubRoute("Administration", "Users")),
          cloneRoute(findSubRoute("Administration", "Roles & Permissions")),
          cloneRoute(findSubRoute("Administration", "Linked Roles")),
        ]),
        compactCategory("Organization", "/setup/organization", null, [
          cloneRoute(findSubRoute("Organization", "Departments")),
          cloneRoute(findSubRoute("Organization", "Panel"), { name: "Panels" }),
        ]),
        compactCategory("System", "/setup/system", null, [
          cloneRoute(findSubRoute("Administration", "Releases"), { name: "System Updates" }),
        ]),
      ]);

      return [
        cloneRoute(dashboardRoute),
        cloneRoute(todoRoute),
        cloneRoute(performanceRoute),
        recruitmentSection,
        reportsSection,
        knowledgeBaseSection,
        setupSection,
      ].filter(Boolean);
    } catch (error) {
      console.error("Error filtering sidebar routes:", error);
      return [];
    }
  }, [userRole, user, hasViewAccess, isReportingManager, isAdminUser]);

  // Filter routes for ROUTING (includes hidden routes)
  const filteredAllRoutes = useMemo(() => {
    if (!mainRoutes || !Array.isArray(mainRoutes)) {
      console.warn(
        "mainRoutes is not available or not an array for routing:",
        mainRoutes
      );
      return [];
    }

    try {
      const filtered = mainRoutes
        .filter((route) => {
          // DON'T skip hidden routes for routing - they need to be accessible

          // For reviewers, special handling FIRST (before layout check)
          if (userRole === "reviewer") {
            // Allow Dashboard
            if (route.name === "Dashboard") {
              return true;
            }
            // Allow Todo
            if (route.name === "Todo") {
              return true;
            }
            // Allow Document Verification and Onboarding Checklist modules directly
            if (route.module === "Document Verification" || route.module === "Onboarding Checklist") {
              return true;
            }
            // Check module permissions for performance modules
            if (route.module && route.path && route.path.startsWith('/performance/')) {
              return hasViewAccess(user, route.module);
            }
            // Allow candidate-create routes for document verification workflow
            if (route.path && route.path.includes('/recruitment/candidate-create')) {
              return true;
            }
            // Allow application-create routes for document verification workflow
            if (route.path && route.path.includes('/recruitment/application-create')) {
              return true;
            }
            // For routes with subRoutes, check if any subRoute is accessible
            if (route.subRoutes && Array.isArray(route.subRoutes)) {
              const hasAllowedModule = route.subRoutes.some(
                (subRoute) => {
                  // Check Document Verification and Onboarding Checklist
                  if (subRoute.module === "Document Verification" || subRoute.module === "Onboarding Checklist") {
                    return true;
                  }
                  // Check Performance modules
                  if (subRoute.module && hasViewAccess(user, subRoute.module)) {
                    return true;
                  }
                  return false;
                }
              );
              if (hasAllowedModule) {
                return true; // Include parent route, subRoutes will be filtered in map
              }
            }
            // Block all other routes for reviewers
            return false;
          }

          // For employees, explicit handling
          if (userRole === "employee") {
            // Allow Dashboard and Todo
            if (route.name === "Dashboard" || route.name === "Todo") {
              return true;
            }
            // Allow Performance category if has accessible subroutes
            if (route.name === "Performance" && route.subRoutes) {
              const hasAccessibleSubroutes = route.subRoutes.some((subRoute) => {
                if (subRoute.layout && Array.isArray(subRoute.layout)) {
                  if (!subRoute.layout.includes(ROLE_PATH.employee)) return false;
                }
                return subRoute.module ? hasViewAccess(user, subRoute.module) : true;
              });
              return hasAccessibleSubroutes;
            }
            // Allow Recruitment category for employee workflows - ALWAYS include this
            if (route.name === "Recruitment" && route.subRoutes) {
              return true;
            }
            // Allow all recruitment-related routes for employees
            if (route.path && route.path.startsWith('/recruitment/')) {
              return true;
            }
            // Check layout
            if (route.layout && Array.isArray(route.layout)) {
              return (
                route.layout.includes(ROLE_PATH.employee) ||
                route.layout.includes(ROLE_PATH.user)
              );
            }
            // For routes without layout, check module permission
            if (route.module) {
              return hasViewAccess(user, route.module);
            }
            return true;
          }

          // Check role access for non-reviewers, non-employees
          if (route.layout && Array.isArray(route.layout)) {
            // Determine allowed role path based on user's active role
            let allowedRolePath = ROLE_PATH.user;
            if (userRole === "superAdmin") {
              allowedRolePath = ROLE_PATH.superAdmin;
            }

            if (
              !route.layout.includes(allowedRolePath) ||
              (route.layout.includes(ROLE_PATH.employeeNotAllowed) &&
                userRole === "employee")
            )
              return false;
          }

          // Check module permissions for non-reviewers
          if (route.isCategory && route.subRoutes && Array.isArray(route.subRoutes)) {
            const hasAnySubrouteAccess = route.subRoutes.some((subRoute) => {
              if (subRoute.hidden) return false;
              if (subRoute.adminOnly) return isAdminUser;
              return subRoute.module ? hasViewAccess(user, subRoute.module) : true;
            });
            return hasAnySubrouteAccess;
          }

          if (route.module && userRole !== "superAdmin") {
            return hasViewAccess(user, route.module);
          }

          return true;
        })
        .map((route) => {
          // Filter subRoutes if they exist
          if (route.subRoutes && Array.isArray(route.subRoutes)) {
            return {
              ...route,
              subRoutes: route.subRoutes.filter((subRoute) => {
                // For reviewers, allow Document Verification, Onboarding Checklist, and Performance modules with access
                if (userRole === "reviewer") {
                  // Check if it's one of the allowed modules
                  const isRecruitmentModule = subRoute.module === "Document Verification" || subRoute.module === "Onboarding Checklist";

                  // Check if it's a Performance module with view access
                  const isPerformanceModule = subRoute.module && hasViewAccess(user, subRoute.module);

                  // Check if has reviewer in layout (if layout is specified)
                  const hasReviewerAccess = !subRoute.layout || (Array.isArray(subRoute.layout) && subRoute.layout.includes(ROLE_PATH.reviewer));

                  return (isRecruitmentModule || isPerformanceModule) && hasReviewerAccess;
                }

                // For employees, check layout and module permissions
                if (userRole === "employee") {
                  // Always allow recruitment subroutes for employees
                  if (route.name === "Recruitment" || (route.path && route.path.startsWith('/recruitment/'))) {
                    return true;
                  }
                  if (subRoute.layout && Array.isArray(subRoute.layout)) {
                    if (
                      !subRoute.layout.includes(ROLE_PATH.employee) &&
                      !subRoute.layout.includes(ROLE_PATH.user)
                    ) {
                      return false;
                    }
                  }
                  if (subRoute.module) {
                    return hasViewAccess(user, subRoute.module);
                  }
                  return true;
                }

                // Check if route is admin-only
                if (subRoute.adminOnly && !isAdminUser) {
                  return false;
                }
                if (subRoute.module && !isAdminUser) {
                  return hasViewAccess(user, subRoute.module);
                }
                return true;
              }),
            };
          }
          return route;
        });

      return filtered || [];
    } catch (error) {
      console.error("Error filtering all routes:", error);
      return [];
    }
  }, [userRole, user, hasViewAccess, isAdminUser]);

  // Keep sidebar routes in sync with filteredSidebarRoutes.
  // Important: `isReportingManager` is resolved async; avoid guards that only look at pathname/length,
  // or the sidebar may not update until the next navigation.
  useEffect(() => {
    if (Array.isArray(filteredSidebarRoutes)) {
      setSubRoute(filteredSidebarRoutes);
    } else {
      console.error("filteredSidebarRoutes is not an array:", filteredSidebarRoutes);
      setSubRoute([]);
    }
  }, [filteredSidebarRoutes]);

  // Fetch initial data only once
  useEffect(() => {
    if (!isInitializedRef.current) {
      const fetchData = async () => {
        try {
          await Promise.all([
            dispatch(fetchRouteData()),
            dispatch(fetchImage()),
          ]);
        } catch (error) {
          console.error("Error dispatching actions:", error);
        }
      };

      fetchData();
      isInitializedRef.current = true;
    }
  }, [dispatch]);

  // Handle sidebar body class
  useEffect(() => {
    if (openSidebar) {
      document.body.classList.add("openSidebar");
    } else {
      document.body.classList.remove("openSidebar");
    }
    return () => {
      document.body.classList.remove("openSidebar");
    };
  }, [openSidebar]);

  // Route utility functions
  const getRoute = useCallback(() => {
    return window.location.pathname !== "/admin/full-screen-maps";
  }, []);

  const getActiveRoute = useCallback(
    (routes) => {
      if (!routes || !Array.isArray(routes)) {

        return "Dashboard";
      }

      let activeRoute = "Dashboard";
      const currentPath = location.pathname.replace(/^#/, ""); // Remove hash if present

      const findActiveRoute = (routeList) => {
        if (!Array.isArray(routeList)) return "Dashboard";

        for (let route of routeList) {
          if (route.subRoutes && Array.isArray(route.subRoutes)) {
            const subActiveRoute = findActiveRoute(route.subRoutes);
            if (subActiveRoute !== "Dashboard") {
              return subActiveRoute;
            }
          } else if (route.path) {
            if (
              currentPath === route.path ||
              currentPath.startsWith(route.path + "/")
            ) {
              return route.name;
            }

            const pathSegments = currentPath.split("/").filter(Boolean);
            const routeSegments = route.path.split("/").filter(Boolean);

            if (pathSegments.length >= routeSegments.length) {
              let matches = true;
              for (let i = 0; i < routeSegments.length; i++) {
                if (routeSegments[i] !== pathSegments[i]) {
                  matches = false;
                  break;
                }
              }

              if (matches) {
                const routeBase = route.path.split("/").pop();
                const currentBase = pathSegments[routeSegments.length];

                if (currentBase && routeBase) {
                  const singularBase = routeBase.endsWith("s")
                    ? routeBase.slice(0, -1)
                    : routeBase;
                  if (currentBase.startsWith(singularBase)) {
                    return route.name;
                  }
                }
              }
            }
          }
        }
        return "Dashboard";
      };

      activeRoute = findActiveRoute(routes);

      return activeRoute;
    },
    [location.pathname]
  );

  // Updated getRoutes function to use ALL routes (including hidden ones)
  const getRoutes = useCallback(
    (routes) => {
      if (!filteredAllRoutes || !Array.isArray(filteredAllRoutes)) {
        console.warn(
          "getRoutes: filteredAllRoutes is not available or not an array"
        );
        return [];
      }

      const renderRoute = (route, key) => {
        if (!route) return null;

        // Check if route is accessible to current user role
        if (route.layout && Array.isArray(route.layout)) {
          let hasAccess = false;

          if (userRole === "superAdmin") {
            hasAccess = route.layout.includes(ROLE_PATH.superAdmin);
          } else if (userRole === "reviewer") {
            hasAccess = route.layout.includes(ROLE_PATH.reviewer);
            // For reviewers, allow access to candidate-create routes even if module permissions are restrictive
            // This is needed for document verification workflow
            if (!hasAccess && route.path && route.path.includes('/recruitment/candidate-create')) {
              hasAccess = route.layout.includes(ROLE_PATH.reviewer);
            }
          } else if (userRole === "employee") {
            hasAccess = route.layout.includes(ROLE_PATH.employee) || route.layout.includes(ROLE_PATH.user);
          } else {
            hasAccess = route.layout.includes(ROLE_PATH.user);
          }

          if (!hasAccess) {
            return null;
          }
        }

        // Check if route blocks employees and user's ACTIVE role is employee
        if (
          route.layout &&
          Array.isArray(route.layout) &&
          route.layout.includes(ROLE_PATH.employeeNotAllowed) &&
          userRole === "employee"
        ) {
          return null;
        }

        if (route.component) {
          return (
            <Route path={route.path} element={<route.component />} key={key} />
          );
        }

        return null;
      };

      const allRoutes = [];

      const processRoutes = (routeList) => {
        if (!Array.isArray(routeList)) return;

        routeList.forEach((route, index) => {
          if (!route) return;

          const renderedRoute = renderRoute(route, `route-${index}`);
          if (renderedRoute) {
            if (route.path === "/my-reviews") {
              return;
            }
            allRoutes.push(renderedRoute);
          }

          if (route.subRoutes && Array.isArray(route.subRoutes)) {
            processRoutes(route.subRoutes);
          }
        });
      };

      processRoutes(filteredAllRoutes);

      return allRoutes;
    },
    [userRole, filteredAllRoutes]
  );

  // Memoize route classifications
  const defaultRoute = useMemo(() => {
    return ["/dashboard"].some((path) => location.pathname.includes(path));
  }, [location.pathname]);

  const topRoute = useMemo(() => {
    return ["/create", "/edit"].some((path) =>
      location.pathname.includes(path)
    );
  }, [location.pathname]);

  // Memoize sidebar context value
  const sidebarContextValue = useMemo(
    () => ({
      toggleSidebar,
      setToggleSidebar,
    }),
    [toggleSidebar, setToggleSidebar]
  );

  // Redirect logic for unauthorized users
  if (!user) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  // Early return if routes are not loaded yet
  if (!mainRoutes || !Array.isArray(mainRoutes)) {
    return (
      <Box>
        <Flex
          justifyContent="center"
          alignItems="center"
          width="100%"
          height="100vh"
        >
          <div>Loading routes...</div>
        </Flex>
      </Box>
    );
  }

  document.documentElement.dir = "ltr";

  return (
    <Box>
      <SidebarContext.Provider value={sidebarContextValue}>
        <Sidebar
          subRoute={subRoute || []}
          isUserLayout={userRole !== "superAdmin"}
          routes={subRoute || []}
          largeLogo={largeLogo || []}
          display="none"
          {...rest}
          openSubBar={openSubBar}
          setOpenSubBar={setOpenSubBar}
          openSidebar={openSidebar}
          setOpenSidebar={setOpenSidebar}
        />
        <FilterSidebar />
        <Box
          className="dash-cont"
          float="right"
          minHeight="100vh"
          height="100%"
          overflow="auto"
          position="relative"
          maxHeight="100%"
          ml={{
            base: "0",
            xl: openSidebar ? "280px" : "80px",
          }}
          w={{
            base: "100%",
            xl: openSidebar ? "calc(100% - 280px)" : "calc(100% - 80px)",
          }}
          maxWidth={{
            base: "100%",
            xl: openSidebar ? "calc(100% - 280px)" : "calc(100% - 80px)",
          }}
          transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
          transitionDuration=".2s, .2s, .35s"
          transitionProperty="top, bottom, width, margin"
          transitionTimingFunction="linear, linear, ease"
        >
          <Portal>
            <Box className="header">
              <Navbar
                isUserLayout={userRole !== "superAdmin"}
                onOpen={onOpen}
                logoText={
                  userRole === "superAdmin" ? "Admin Dashboard" : "HR Dashboard"
                }
                brandText={getActiveRoute(subRoute)}
                routes={subRoute || []}
                subRoute={subRoute || []}
                fixed={fixed}
                largeLogo={largeLogo || []}
                openSubBar={openSubBar}
                setOpenSubBar={setOpenSubBar}
                openSidebar={openSidebar}
                setOpenSidebar={setOpenSidebar}
                {...rest}
              />
            </Box>
          </Portal>
          <Box
            pt={{ base: "150px", md: "95px", xl: "95px" }}
            className={`main-content ${defaultRoute ? "defaultPage" : ""} ${topRoute ? "ptNormal" : ""
              }`}
          >
            {getRoute() ? (
              <Box
                mx="auto"
                pe="20px"
                w="100%"
                minH="84vh"
                pt="20px"
                px={{ base: "20px", md: "30px", xl: "40px" }}
              >
                <Suspense
                  fallback={
                    <Flex
                      justifyContent="center"
                      alignItems="center"
                      width="100%"
                    >
                      <div>Loading...</div>
                    </Flex>
                  }
                >
                  <Routes>
                    {getRoutes(subRoute)}
                    {/* Root route redirect */}
                    <Route
                      path="/"
                      element={
                        <Navigate
                          to={
                            userRole === "reviewer"
                              ? "/recruitment/document-verification"
                              : "/dashboard"
                          }
                          replace
                        />
                      }
                    />
                    {/* Catch-all route - redirect to default based on role */}
                    <Route
                      path="*"
                      element={
                        <Navigate
                          to={
                            userRole === "reviewer"
                              ? "/recruitment/document-verification"
                              : "/dashboard"
                          }
                          replace
                        />
                      }
                    />
                  </Routes>
                </Suspense>
              </Box>
            ) : null}
          </Box>
          <Box>
            <Footer />
          </Box>
        </Box>
      </SidebarContext.Provider>
    </Box>
  );
}
